/**
 * 部门KPI权重配置页面（重构版）
 * 从单文件448行优化为~120行
 */

import React, { useState } from 'react';
import { Table, Select, Button, Space, Tag, Card, Tooltip } from 'antd';
import { SaveOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { Department } from '../../../services/kpiSettingsService';
import PeriodConfigModal from './PeriodConfigModal';
import { useDeptKPILogic, KPISelectorCell, WeightInputCell } from './KPISettingsModule';

const DeptKPISettings: React.FC = () => {
    const [periodModalVisible, setPeriodModalVisible] = useState(false);

    const {
        departments,
        periods,
        dimensions,
        selectedPeriodId,
        editingData,
        loading,
        saving,
        setSelectedPeriodId,
        handleChange,
        getTotalWeight,
        handleSave,
        handleCopyFromPrevious,
        loadProfiles,
        loadBaseData,
    } = useDeptKPILogic();

    // 表格列定义
    const columns = [
        {
            title: '部门',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            fixed: 'left' as const,
        },
        ...(['kpi1', 'kpi2', 'kpi3', 'kpi4'] as const).map((kpiKey, idx) => ({
            title: `KPI ${idx + 1}`,
            key: kpiKey,
            width: 220,
            render: (_: any, record: Department) => (
                <Space size={4}>
                    <KPISelectorCell
                        deptId={record.id}
                        deptName={record.name}
                        kpiField={kpiKey}
                        value={(editingData[record.id] as any)?.[kpiKey]}
                        dimensions={dimensions}
                        onChange={handleChange}
                    />
                    <WeightInputCell
                        deptId={record.id}
                        weightField={`${kpiKey}_weight`}
                        value={(editingData[record.id] as any)?.[`${kpiKey}_weight`]}
                        onChange={handleChange}
                    />
                </Space>
            ),
        })),
        {
            title: '工作态度',
            key: 'attitude',
            width: 100,
            render: (_: any, record: Department) => (
                <WeightInputCell
                    deptId={record.id}
                    weightField="attitude_weight"
                    value={(editingData[record.id] as any)?.attitude_weight}
                    onChange={handleChange}
                />
            ),
        },
        {
            title: '总权重',
            key: 'total',
            width: 100,
            render: (_: any, record: Department) => {
                const total = getTotalWeight(record.id);
                const isValid = Math.abs(total - 100) < 0.01 || total === 0;
                return (
                    <Tag color={isValid ? (Math.abs(total - 100) < 0.01 ? 'success' : 'default') : 'error'}>
                        {total}%
                    </Tag>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card
                title="部门KPI权重配置"
                extra={
                    <Space>
                        <span>考核周期：</span>
                        <Select
                            style={{ width: 180 }}
                            value={selectedPeriodId}
                            onChange={setSelectedPeriodId}
                            placeholder="选择周期"
                        >
                            {periods.map(p => (
                                <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>
                            ))}
                        </Select>
                        <Button icon={<CopyOutlined />} onClick={handleCopyFromPrevious}>复制上期</Button>
                        <Button icon={<ReloadOutlined />} onClick={loadProfiles}>刷新</Button>
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                            保存配置
                        </Button>
                    </Space>
                }
            >
                <div style={{ marginBottom: 16, color: '#666' }}>
                    <Tooltip title="每个部门最多可配置4个KPI指标，权重总和必须等于100%">
                        <span>💡 提示：每个部门最多4个KPI，权重总和须为100%（建议保留15%工作态度分）</span>
                    </Tooltip>
                    <span style={{ marginLeft: 20, color: '#999', fontSize: 12 }}>
                        (已加载指标: {dimensions.filter(d => d.is_active).length} 个)
                    </span>
                </div>

                <Table
                    dataSource={departments}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 1300 }}
                    bordered
                    size="middle"
                />
            </Card>

            <PeriodConfigModal
                visible={periodModalVisible}
                onCancel={() => {
                    setPeriodModalVisible(false);
                    loadBaseData();
                }}
            />
        </div>
    );
};

export default DeptKPISettings;
